/**
 * Whether the server answered lately, as a second opinion on `navigator.onLine`.
 *
 * Request of 5 October 2026: a person online and working was told "You're
 * offline", and it never went away. The browser's flag is known to stick on
 * false in an app resumed from the home screen (iOS above all), with no
 * `online` event to follow. A request the server answers (with any status) is
 * proof of a connection, so it clears the strip whatever the flag says.
 */

let answeredAt = 0;
let wentOfflineAt = 0;
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

/** The server answered a request. Called by the HTTP client on every response. */
export function reportAnswered(): void {
  const wasOffline = answeredAt <= wentOfflineAt;
  answeredAt = Date.now();
  if (wasOffline) emit();
}

/** The browser said it went offline. */
export function reportWentOffline(): void {
  wentOfflineAt = Date.now();
  emit();
}

/** Online unless the browser says otherwise AND nothing has answered since. */
export function isReachable(browserOnline: boolean): boolean {
  return browserOnline || answeredAt > wentOfflineAt;
}

export function onReachabilityChange(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** For tests: back to knowing nothing. */
export function resetReachability(): void {
  answeredAt = 0;
  wentOfflineAt = 0;
}
